import * as React from "react";
import { useMutation, useAction } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { ExternalLink, FileText, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAromas, useFlavors, useOrigins, useRegions, useProducers } from "@/hooks/useReferenceData";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { useAppLocale } from "@/lib/i18n";
import {
  prepareProductImageForUpload,
  PRODUCT_IMAGE_HELP_TEXT,
  PRODUCT_IMAGE_INPUT_ACCEPT,
  PRODUCT_IMAGE_TOO_LARGE_MESSAGE,
  validateProductImageFile,
} from "@/lib/product-image-upload";
import {
  prepareProductInfoSheetForUpload,
  PRODUCT_INFO_SHEET_HELP_TEXT,
  PRODUCT_INFO_SHEET_INPUT_ACCEPT,
  PRODUCT_INFO_SHEET_TOO_LARGE_MESSAGE,
  validateProductInfoSheetFile,
} from "@/lib/product-info-sheet-upload";
import { m } from "@/paraglide/messages.js";

import { normalizeTastingNotes } from "./product-tasting-notes";
import { RegionManagementDialog } from "./region-management-dialog";
import { AromaManagementDialog, FlavorManagementDialog } from "./tasting-note-management-dialog";
import { TastingNotesSelect } from "./tasting-notes-select";

interface ProductEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: any;
}

export function ProductEditDialog({ open, onOpenChange, product }: ProductEditDialogProps) {
  const { locale } = useAppLocale();
  const [form, setForm] = React.useState(toForm(product));
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [infoSheetDragOver, setInfoSheetDragOver] = React.useState(false);
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [infoSheetFile, setInfoSheetFile] = React.useState<File | null>(null);
  const [removeInfoSheetOnSave, setRemoveInfoSheetOnSave] = React.useState(false);
  const [regionsOpen, setRegionsOpen] = React.useState(false);
  const [flavorsOpen, setFlavorsOpen] = React.useState(false);
  const [aromasOpen, setAromasOpen] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const infoSheetInputRef = React.useRef<HTMLInputElement>(null);
  const bulkUpdate = useMutation(api.products.bulkUpdateProducts);
  const updateImage = useAction(api.products.updateProductImage);
  const updateInfoSheet = useAction(api.products.updateProductInfoSheet);
  const removeInfoSheet = useAction(api.products.removeProductInfoSheet);
  const origins = useOrigins();
  const regions = useRegions();
  const producers = useProducers();
  const flavors = useFlavors();
  const aromas = useAromas();

  const imagePreviewUrl = React.useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile],
  );

  React.useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  React.useEffect(() => {
    if (product && open) {
      setForm(toForm(product));
      setImageFile(null);
      setInfoSheetFile(null);
      setRemoveInfoSheetOnSave(false);
    }
  }, [product, open]);

  const set = <K extends keyof ReturnType<typeof toForm>>(key: K, value: ReturnType<typeof toForm>[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(m.product_name_required());
      return;
    }

    const parsedWeight = Number(form.weight);
    if (!form.weight.trim() || !Number.isFinite(parsedWeight) || parsedWeight <= 0) {
      toast.error("Weight is required and must be greater than 0.");
      return;
    }

    setSaving(true);
    try {
      const id = product._id ?? product.id;
      await bulkUpdate({
        updates: [{
          id,
          fields: {
            name: form.name.trim(),
            description: form.description || undefined,
            originId: form.originId || undefined,
            regionId: form.regionId || undefined,
            producerId: form.producerId || undefined,
            varietal: form.varietal || undefined,
            millingProcess: form.millingProcess || undefined,
            masl: form.masl || undefined,
            harvestCycle: form.harvestCycle || undefined,
            acidity: form.acidity || undefined,
            bodyFeel: form.bodyFeel || undefined,
            flavorNames: normalizeTastingNotes(form.flavorNotes),
            aromaNames: normalizeTastingNotes(form.aromaNotes),
            weight: parsedWeight,
            score: form.score ? Number(form.score) : undefined,
            grainPro: form.grainPro,
            onTheWater: form.onTheWater,
            waterActivity: form.waterActivity ? Number(form.waterActivity) : undefined,
            moisturePercentage: form.moisturePercentage ? Number(form.moisturePercentage) : undefined,
            agtronInternal: form.agtronInternal ? Number(form.agtronInternal) : undefined,
            samplesStock: form.samplesStock === "" ? undefined : Number(form.samplesStock),
            lastCupDate: form.lastCupDate || undefined,
            greenNotes: form.greenNotes || undefined,
            pertinentInfo: form.pertinentInfo || undefined,
            recommendedProfile: form.recommendedProfile || undefined,
            archived: form.archived,
          },
        }],
      });

      if (imageFile) {
        setUploading(true);
        try {
          const preparedImage = await prepareProductImageForUpload(imageFile);
          await updateImage({
            productId: id,
            fileData: preparedImage.fileData,
            contentType: preparedImage.contentType,
            filename: preparedImage.filename,
          });
        } catch (error) {
          toast.error(
            toUserFacingErrorMessage(error, "Product updated, but image upload failed", {
              unauthorizedMessage: m.inventory_products_unauthorized(),
              oversizeMessage: PRODUCT_IMAGE_TOO_LARGE_MESSAGE,
            }),
          );
          return;
        } finally {
          setUploading(false);
        }
      }

      if (infoSheetFile) {
        setUploading(true);
        try {
          const preparedInfoSheet = await prepareProductInfoSheetForUpload(infoSheetFile);
          await updateInfoSheet({
            productId: id,
            fileData: preparedInfoSheet.fileData,
            contentType: preparedInfoSheet.contentType,
            filename: preparedInfoSheet.filename,
          });
        } catch (error) {
          toast.error(
            toUserFacingErrorMessage(error, "Product updated, but info sheet upload failed", {
              unauthorizedMessage: m.inventory_products_unauthorized(),
              oversizeMessage: PRODUCT_INFO_SHEET_TOO_LARGE_MESSAGE,
            }),
          );
          return;
        } finally {
          setUploading(false);
        }
      } else if (removeInfoSheetOnSave) {
        setUploading(true);
        try {
          await removeInfoSheet({ productId: id });
        } catch (error) {
          toast.error(
            toUserFacingErrorMessage(error, "Product updated, but info sheet removal failed", {
              unauthorizedMessage: m.inventory_products_unauthorized(),
            }),
          );
          return;
        } finally {
          setUploading(false);
        }
      }

      setImageFile(null);
      setInfoSheetFile(null);
      setRemoveInfoSheetOnSave(false);
      toast.success(m.product_updated());
      onOpenChange(false);
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to update product", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = (file: File) => {
    const validationError = validateProductImageFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setImageFile(file);
  };

  const handleInfoSheetSelect = (file: File) => {
    const validationError = validateProductInfoSheetFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setInfoSheetFile(file);
    setRemoveInfoSheetOnSave(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageSelect(file);
  };

  const handleInfoSheetDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setInfoSheetDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleInfoSheetSelect(file);
  };

  const handleRemoveInfoSheet = () => {
    setInfoSheetFile(null);
    setRemoveInfoSheetOnSave(Boolean(product?.downloadableInfoSheet?.url));
  };

  const imageUrl = typeof product?.imageMetadata === "object" && product.imageMetadata?.url
    ? product.imageMetadata.url
    : typeof product?.imageMetadata === "string"
      ? product.imageMetadata
      : null;
  const displayedImageUrl = imagePreviewUrl ?? imageUrl;
  const currentInfoSheet = product?.downloadableInfoSheet?.url && !removeInfoSheetOnSave
    ? product.downloadableInfoSheet
    : null;

  const filteredRegions = React.useMemo(() => {
    if (!regions) return [];
    if (!form.originId) return regions;
    return regions.filter((r: any) => r.originId === form.originId);
  }, [regions, form.originId]);

  const filteredProducers = React.useMemo(() => {
    if (!producers) return [];
    if (!form.originId && !form.regionId) return producers;
    return producers.filter((p: any) => {
      if (form.regionId && p.regionId !== form.regionId) return false;
      if (form.originId && p.originId !== form.originId) return false;
      return true;
    });
  }, [producers, form.originId, form.regionId]);

  const originNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    (origins ?? []).forEach((origin: any) => {
      map.set(origin._id, origin.name);
    });
    return map;
  }, [origins]);

  const regionNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    (regions ?? []).forEach((region: any) => {
      map.set(region._id, region.name);
    });
    return map;
  }, [regions]);

  const originOptions = React.useMemo(
    () => (origins ?? []).map((origin: any) => ({ value: origin._id, label: origin.name })),
    [origins],
  );

  const regionOptions = React.useMemo(
    () => filteredRegions.map((region: any) => ({
      value: region._id,
      label: region.name,
      sublabel: region.originId ? originNameMap.get(region.originId) : undefined,
      searchText: [region.name, region.originId ? originNameMap.get(region.originId) : undefined]
        .filter(Boolean)
        .join(" "),
    })),
    [filteredRegions, originNameMap],
  );

  const producerOptions = React.useMemo(
    () => filteredProducers.map((producer: any) => ({
      value: producer._id,
      label: producer.name,
      sublabel: [
        producer.regionId ? regionNameMap.get(producer.regionId) : undefined,
        producer.originId ? originNameMap.get(producer.originId) : undefined,
      ]
        .filter(Boolean)
        .join(" • ") || undefined,
      searchText: [
        producer.name,
        producer.regionId ? regionNameMap.get(producer.regionId) : undefined,
        producer.originId ? originNameMap.get(producer.originId) : undefined,
      ]
        .filter(Boolean)
        .join(" "),
    })),
    [filteredProducers, originNameMap, regionNameMap],
  );

  React.useEffect(() => {
    if (!form.regionId || filteredRegions.some((region: any) => region._id === form.regionId)) {
      return;
    }

    setForm((prev) => ({ ...prev, regionId: "" }));
  }, [filteredRegions, form.regionId]);

  React.useEffect(() => {
    if (!form.producerId || filteredProducers.some((producer: any) => producer._id === form.producerId)) {
      return;
    }

    setForm((prev) => ({ ...prev, producerId: "" }));
  }, [filteredProducers, form.producerId]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{m.edit_product()}</DialogTitle>
          <DialogDescription>{m.edit_product_description()}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="mt-2">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="basic">{m.tab_basic()}</TabsTrigger>
            <TabsTrigger value="coffee">{m.tab_coffee()}</TabsTrigger>
            <TabsTrigger value="quality">{m.tab_quality()}</TabsTrigger>
            <TabsTrigger value="notes">{m.notes()}</TabsTrigger>
            <TabsTrigger value="image">{m.image()}</TabsTrigger>
            <TabsTrigger value="info-sheet">Info sheet</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 pt-4">
            <div className="grid gap-2">
              <Label htmlFor="ep-name">{m.name_required()}</Label>
              <Input id="ep-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>{m.description()}</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>{m.origin()}</Label>
              <ComboboxSelect
                options={originOptions}
                value={form.originId}
                onValueChange={(value) => set("originId", value)}
                placeholder={m.select_origin()}
                searchPlaceholder="Search origins..."
                emptyText="No origins found."
                loading={origins === undefined}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>{m.region()}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto px-0 text-xs"
                  onClick={() => setRegionsOpen(true)}
                >
                  Manage regions
                </Button>
              </div>
              <ComboboxSelect
                options={regionOptions}
                value={form.regionId}
                onValueChange={(value) => set("regionId", value)}
                placeholder={m.select_region()}
                searchPlaceholder="Search regions..."
                emptyText="No matching regions found. Use Manage regions to add one."
                loading={regions === undefined}
              />
            </div>
            <div className="grid gap-2">
              <Label>{m.producer()}</Label>
              <ComboboxSelect
                options={producerOptions}
                value={form.producerId}
                onValueChange={(value) => set("producerId", value)}
                placeholder={m.select_producer()}
                searchPlaceholder="Search producers..."
                emptyText="No producers found."
                loading={producers === undefined}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.archived} onCheckedChange={(v) => set("archived", v)} />
              <Label>{m.status_archived()}</Label>
            </div>
          </TabsContent>

          <TabsContent value="coffee" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.varietal()}</Label>
                <Input value={form.varietal} onChange={(e) => set("varietal", e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>{m.milling_process()}</Label>
                <Input value={form.millingProcess} onChange={(e) => set("millingProcess", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.masl()}</Label>
                <Input value={form.masl} onChange={(e) => set("masl", e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>{m.harvest_cycle()}</Label>
                <Input value={form.harvestCycle} onChange={(e) => set("harvestCycle", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.acidity()}</Label>
                <Input value={form.acidity} onChange={(e) => set("acidity", e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>{m.body_feel()}</Label>
                <Input value={form.bodyFeel} onChange={(e) => set("bodyFeel", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.weight_lbs()}</Label>
                <Input type="number" min="0.01" step="0.01" value={form.weight} onChange={(e) => set("weight", e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>{m.score()}</Label>
                <Input type="number" value={form.score} onChange={(e) => set("score", e.target.value)} />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.grainPro} onCheckedChange={(v) => set("grainPro", v)} />
                <Label>{m.grainpro()}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.onTheWater} onCheckedChange={(v) => set("onTheWater", v)} />
                <Label>{m.on_the_water()}</Label>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="quality" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.water_activity()}</Label>
                <Input type="number" step="0.01" value={form.waterActivity} onChange={(e) => set("waterActivity", e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>{m.moisture_percentage()}</Label>
                <Input type="number" step="0.1" value={form.moisturePercentage} onChange={(e) => set("moisturePercentage", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.agtron_internal()}</Label>
                <Input type="number" value={form.agtronInternal} onChange={(e) => set("agtronInternal", e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>{m.samples_stock()}</Label>
                <Input type="number" value={form.samplesStock} onChange={(e) => set("samplesStock", e.target.value)} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>{m.last_cup_date()}</Label>
              <DatePicker value={form.lastCupDate} onChange={(v) => set("lastCupDate", v)} placeholder={m.select_date()} />
            </div>
          </TabsContent>

          <TabsContent value="notes" className="space-y-4 pt-4">
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Aroma notes</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto px-0 text-xs"
                  onClick={() => setAromasOpen(true)}
                >
                  {locale === "es" ? "Administrar aromas" : "Manage Aromas"}
                </Button>
              </div>
              <TastingNotesSelect
                value={form.aromaNotes}
                onChange={(value) => set("aromaNotes", value)}
                options={(aromas ?? []).map((item) => item.name ?? "").filter(Boolean)}
                placeholder="Select or add aroma notes"
                searchPlaceholder="Search aromas..."
                emptyText="No aromas found."
                loading={aromas === undefined}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Flavor notes</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto px-0 text-xs"
                  onClick={() => setFlavorsOpen(true)}
                >
                  {locale === "es" ? "Administrar sabores" : "Manage Flavors"}
                </Button>
              </div>
              <TastingNotesSelect
                value={form.flavorNotes}
                onChange={(value) => set("flavorNotes", value)}
                options={(flavors ?? []).map((item) => item.name ?? "").filter(Boolean)}
                placeholder="Select or add flavor notes"
                searchPlaceholder="Search flavors..."
                emptyText="No flavors found."
                loading={flavors === undefined}
              />
            </div>
            <div className="grid gap-2">
              <Label>{m.green_notes()}</Label>
              <Textarea value={form.greenNotes} onChange={(e) => set("greenNotes", e.target.value)} rows={3} />
            </div>
            <div className="grid gap-2">
              <Label>{m.pertinent_info()}</Label>
              <Textarea value={form.pertinentInfo} onChange={(e) => set("pertinentInfo", e.target.value)} rows={3} />
            </div>
            <div className="grid gap-2">
              <Label>{m.recommended_profile()}</Label>
              <Textarea value={form.recommendedProfile} onChange={(e) => set("recommendedProfile", e.target.value)} rows={3} />
            </div>
          </TabsContent>

          <TabsContent value="image" className="space-y-4 pt-4">
            {displayedImageUrl && (
              <div className="mb-4">
                <Label className="mb-2 block">{imagePreviewUrl ? m.image() : m.current_image()}</Label>
                <img src={displayedImageUrl} alt={product?.name} className="h-40 w-auto rounded-md border object-cover" />
                {imagePreviewUrl && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Preview shown before upload. Save changes to apply the new image.
                  </p>
                )}
              </div>
            )}
            <label
              htmlFor="edit-product-image-upload"
              className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors ${
                dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{m.drag_drop_image_or()}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={saving || uploading}
              >
                {uploading ? m.uploading() : m.choose_file()}
              </Button>
              <input
                id="edit-product-image-upload"
                ref={fileInputRef}
                type="file"
                accept={PRODUCT_IMAGE_INPUT_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageSelect(file);
                }}
              />
              <p className="text-xs text-muted-foreground">{PRODUCT_IMAGE_HELP_TEXT}</p>
            </label>
          </TabsContent>

          <TabsContent value="info-sheet" className="space-y-4 pt-4">
            {currentInfoSheet && (
              <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">
                    {currentInfoSheet.filename || currentInfoSheet.name || "Current product info sheet"}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button type="button" variant="outline" size="sm" asChild>
                    <a href={currentInfoSheet.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Open
                    </a>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={handleRemoveInfoSheet} disabled={saving || uploading}>
                    <X className="h-4 w-4" />
                    Remove
                  </Button>
                </div>
              </div>
            )}
            {removeInfoSheetOnSave && !infoSheetFile && (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                Info sheet will be removed when you save changes.
              </p>
            )}
            {infoSheetFile && (
              <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">{infoSheetFile.name}</span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setInfoSheetFile(null)} disabled={saving || uploading}>
                  <X className="h-4 w-4" />
                  Remove
                </Button>
              </div>
            )}
            <label
              htmlFor="edit-product-info-sheet-upload"
              className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors ${
                infoSheetDragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"
              }`}
              onDragOver={(e) => { e.preventDefault(); setInfoSheetDragOver(true); }}
              onDragLeave={() => setInfoSheetDragOver(false)}
              onDrop={handleInfoSheetDrop}
            >
              <FileText className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Drag and drop a replacement PDF info sheet, or</p>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  infoSheetInputRef.current?.click();
                }}
                disabled={saving || uploading}
              >
                {uploading ? m.uploading() : m.choose_file()}
              </Button>
              <input
                id="edit-product-info-sheet-upload"
                ref={infoSheetInputRef}
                type="file"
                accept={PRODUCT_INFO_SHEET_INPUT_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleInfoSheetSelect(file);
                }}
              />
              <p className="text-xs text-muted-foreground">{PRODUCT_INFO_SHEET_HELP_TEXT}</p>
            </label>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || uploading}>{m.cancel()}</Button>
          <Button onClick={() => void handleSave()} disabled={saving || uploading}>
            {saving || uploading ? m.saving() : m.save_changes()}
          </Button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
      <RegionManagementDialog open={regionsOpen} onOpenChange={setRegionsOpen} />
      <FlavorManagementDialog open={flavorsOpen} onOpenChange={setFlavorsOpen} />
      <AromaManagementDialog open={aromasOpen} onOpenChange={setAromasOpen} />
    </>
  );
}

function toForm(p: any) {
  return {
    name: p?.name ?? "",
    description: p?.description ?? "",
    originId: p?.originId ?? "",
    regionId: p?.regionId ?? "",
    producerId: p?.producerId ?? "",
    flavorNotes: normalizeTastingNotes(
      p?.flavorNames ?? p?.flavors?.map((flavor: any) => flavor?.name),
    ),
    aromaNotes: normalizeTastingNotes(
      p?.aromaNames ?? p?.aromas?.map((aroma: any) => aroma?.name),
    ),
    varietal: p?.varietal ?? "",
    millingProcess: p?.millingProcess ?? "",
    masl: p?.masl ?? "",
    harvestCycle: p?.harvestCycle ?? "",
    acidity: p?.acidity ?? "",
    bodyFeel: p?.bodyFeel ?? "",
    weight: p?.weight != null ? String(p.weight) : "",
    score: p?.score != null ? String(p.score) : "",
    grainPro: p?.grainPro ?? false,
    onTheWater: p?.onTheWater ?? false,
    waterActivity: p?.waterActivity != null ? String(p.waterActivity) : "",
    moisturePercentage: p?.moisturePercentage != null ? String(p.moisturePercentage) : "",
    agtronInternal: p?.agtronInternal != null ? String(p.agtronInternal) : "",
    samplesStock: p?.samplesStock != null ? String(p.samplesStock) : "",
    lastCupDate: p?.lastCupDate ?? "",
    greenNotes: p?.greenNotes ?? "",
    pertinentInfo: p?.pertinentInfo ?? "",
    recommendedProfile: p?.recommendedProfile ?? "",
    archived: p?.archived ?? false,
  };
}
